import React, { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { CalendarCheck, CheckCircle2, TriangleAlert, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import MonthlyRecapPreviewDialog from "@/components/MonthlyRecapPreviewDialog";

const CATEGORY_LABELS = {
  nomina: "Nómina", hipoteca: "Hipoteca", alquiler: "Alquiler", servicios: "Servicios",
  supermercado: "Supermercado", restaurantes: "Restaurantes", transporte: "Transporte",
  gasolina: "Gasolina", salud: "Salud", ocio: "Ocio", viajes: "Viajes", compras: "Compras",
  educacion: "Educación", impuestos: "Impuestos", transferencias: "Transferencias",
  ingresos: "Otros ingresos", cripto: "Cripto", inversiones: "Inversiones", otros: "Otros",
};

const MONTH_NAMES_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const MONTH_NAMES_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function MonthlyRecapCard() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [recap, setRecap] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const currency = user?.base_currency || "USD";
  const lang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const MONTH_NAMES = lang === "es" ? MONTH_NAMES_ES : MONTH_NAMES_EN;

  useEffect(() => {
    api.get("/analytics/monthly-recap").then((r) => setRecap(r.data)).catch(() => setRecap(null));
  }, []);

  if (!recap) return null;
  if (recap.budgets_total === 0) return null;
  if (recap.total_expense === 0 && recap.total_income === 0) return null;

  const [y, m] = recap.month.split("-");
  const label = `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
  const saved = recap.total_saved > 0;

  return (
    <div className="card-surface p-6" data-testid="recap-card">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-[#1C7C54]/10 flex items-center justify-center">
          <CalendarCheck className="w-4 h-4 text-[#1C7C54]" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">{t("recap.eom", { defaultValue: "End of month" })}</p>
          <h3 className="font-display text-lg font-semibold text-[#0A110E]">{t("recap.summaryOf", { defaultValue: "Summary for {{label}}", label })}</h3>
        </div>
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          data-testid="recap-preview-btn"
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-[#1C7C54] border border-[#1C7C54]/30 hover:bg-[#1C7C54]/10 transition-colors"
        >
          <FileText className="w-3.5 h-3.5" />
          {t("recap.preview", { defaultValue: "Preview PDF" })}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="rounded-xl border border-[#EBEBEA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">{t("recap.limitsMet", { defaultValue: "Limits met" })}</div>
          <div className="font-display text-xl font-semibold tabular-nums text-[#0A110E]">
            {recap.budgets_met}/{recap.budgets_total}
          </div>
        </div>
        <div className="rounded-xl border border-[#EBEBEA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">
            {saved ? t("recap.saved", { defaultValue: "Saved" }) : t("recap.exceeded", { defaultValue: "Exceeded" })}
          </div>
          <div className={`font-display text-xl font-semibold tabular-nums ${saved ? "text-[#1C7C54]" : "text-[#D95D39]"}`}>
            {formatCurrency(Math.abs(recap.total_saved), currency)}
          </div>
        </div>
        <div className="rounded-xl border border-[#EBEBEA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">{t("recap.net", { defaultValue: "Net" })}</div>
          <div className={`font-display text-xl font-semibold tabular-nums ${recap.net >= 0 ? "text-[#1C7C54]" : "text-[#D95D39]"}`}>
            {formatCurrency(recap.net, currency)}
          </div>
        </div>
      </div>

      <ul className="divide-y divide-[#EBEBEA]">
        {recap.breakdown.slice(0, 6).map((b) => (
          <li key={b.category} className="py-2 flex items-center gap-3 text-sm">
            {b.met ? (
              <CheckCircle2 className="w-4 h-4 text-[#1C7C54] flex-shrink-0" />
            ) : (
              <TriangleAlert className="w-4 h-4 text-[#D95D39] flex-shrink-0" />
            )}
            <span className="flex-1 text-[#0A110E]">{CATEGORY_LABELS[b.category] || b.category}</span>
            <span className="text-[#5F7368] tabular-nums">
              {formatCurrency(b.used, b.currency)} / {formatCurrency(b.budget, b.currency)}
            </span>
            <span className={`w-16 text-right tabular-nums font-semibold ${b.met ? "text-[#1C7C54]" : "text-[#D95D39]"}`}>
              {b.diff >= 0 ? "+" : ""}{formatCurrency(b.diff, b.currency)}
            </span>
          </li>
        ))}
      </ul>

      <MonthlyRecapPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} />
    </div>
  );
}
